import mustache from "mustache";
import { TextField } from "../../../../implementations/form/text/TextField";
import { FarmTypeSelectorField } from "../form/FarmTypeSelectorField";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { RegistroFincaBorrador } from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";
import { registroFincaTempGrpcService } from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import { parseWizardErrorMessage } from "../../../../../modulos/terceros/wizard/response/WizardValidationErrorResponse";
import {
  BaseRegistroFincaPasoRender,
  RegistroFincaFooterOptions,
} from "./BaseRegistroFincaPasoRender";
import template from "./registroFincaPaso1Template.html";

mustache.parse(template);

/** Paso 1 del wizard de registro: nombre + tipo de finca. "Continuar" crea
 *  el registro temporal en el servidor (o actualiza el que ya existe, si se
 *  volvió a este paso) con `GuardarDatosBasicos`, que además valida que el
 *  nombre no esté repetido entre las fincas del cliente; al guardar bien,
 *  notifica `_onSubmit` con el mismo borrador ya mutado. Los campos
 *  (`TextField`/`FarmTypeSelectorField`) leen/escriben directo sobre el
 *  borrador — hace las veces del `*Request` que antes usaban esos campos
 *  genéricos. */
export class RegistroFincaPaso1Render extends BaseRegistroFincaPasoRender {
  private _nombreField: TextField<RegistroFincaBorrador> | null = null;
  private _tipoField: FarmTypeSelectorField<RegistroFincaBorrador> | null =
    null;

  constructor(
    private readonly _borrador: RegistroFincaBorrador,
    private readonly _onSubmit: (borrador: RegistroFincaBorrador) => void,
  ) {
    super(_borrador.clienteId);
  }

  renderBody(): string {
    this._nombreField = new TextField<RegistroFincaBorrador>(
      this._borrador,
      {
        label: "Nombre de la finca",
        type: "text",
        placeholder: "LA ESPERANZA",
        uppercase: true,
        get: (b) => b.nombre ?? "",
        set: (b, value) => (b.nombre = value),
      },
      { isRequired: true, maxLength: 500 },
    );

    this._tipoField = new FarmTypeSelectorField<RegistroFincaBorrador>(
      this._borrador,
      {
        label: "Tipo de finca",
        get: (b) => b.tipo,
        set: (b, value) => (b.tipo = value),
      },
    );

    return mustache.render(template, {
      nombreField: this._nombreField.render(),
      tipoField: this._tipoField.render(),
    });
  }

  connectBody(): void {
    this._nombreField?.connect();
    this._tipoField?.connect();
  }

  disposeBody(): void {
    this._nombreField?.dispose();
    this._tipoField?.dispose();
  }

  getFooterOptions(): RegistroFincaFooterOptions {
    return {
      submit: { label: "Continuar →", onClick: this._onSubmitClick },
    };
  }

  private _onSubmitClick = (): void => {
    let isValid = true;
    [this._nombreField, this._tipoField].forEach((field) => {
      field?.showError();
      if (field?.getError()) isValid = false;
    });
    if (!isValid) return;

    const btnSubmit = document.getElementById(
      "registroFincaSubmit",
    ) as HTMLButtonElement | null;
    if (btnSubmit) btnSubmit.disabled = true;

    registroFincaTempGrpcService
      .guardarDatosBasicosAsync(this._borrador)
      .then((registro) => {
        this._borrador.registroId = registro.registroId;
        this._borrador.ultimoPaso = registro.ultimoPaso;
        this._borrador.nombre = registro.nombre;
        this._onSubmit(this._borrador);
      })
      .catch((error) => {
        // parseWizardErrorMessage: el error trae el JSON
        // (`{ success, code, message }`, ej. NOMBRE_DUPLICADO), no texto
        // plano — extrae el `message` limpio en vez de mostrar el JSON crudo.
        serviceProvider
          .getErrorService()
          .addError("Error al guardar el nombre", parseWizardErrorMessage(error));
        // Solo en error: en éxito el botón (compartido por todos los pasos)
        // ya pertenece al paso 2, que decide su propio estado inicial.
        if (btnSubmit) btnSubmit.disabled = false;
      });
  };
}
